import React, { useState, useCallback } from 'react';
import {
  Box, Button, Typography, Chip, IconButton, Divider,
  CircularProgress, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import designStudioService from '../../services/designStudioService';

const CATEGORY_COLORS = {
  usability: { bg: '#eff6ff', color: '#1d4ed8' },
  accessibility: { bg: '#f0fdf4', color: '#15803d' },
  consistency: { bg: '#fdf4ff', color: '#7e22ce' },
  visual: { bg: '#fff7ed', color: '#c2410c' },
};

const TweakItem = ({ tweak, onApply, onDismiss, applied }) => {
  const catStyle = CATEGORY_COLORS[tweak.category] || CATEGORY_COLORS.visual;

  return (
    <Box sx={{
      border: '1px solid #eee', borderRadius: 2, mb: 1,
      opacity: applied ? 0.5 : 1,
      background: applied ? '#fafafa' : '#fff',
    }}>
      <Accordion elevation={0} disableGutters sx={{ background: 'transparent', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />} sx={{ px: 1.5, py: 0.5, minHeight: 'auto' }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: 1, pr: 1 }}>
            <Chip
              label={tweak.category}
              size="small"
              sx={{ height: 18, fontSize: 10, ...catStyle, fontWeight: 600, flexShrink: 0, mt: 0.25 }}
            />
            <Typography
              variant="body2"
              sx={{ fontSize: 12, color: '#333', lineHeight: 1.4, textDecoration: applied ? 'line-through' : 'none' }}
            >
              {tweak.description}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1 }}>
          {tweak.elementDescription && (
            <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 0.5, fontSize: 11 }}>
              Target: {tweak.elementDescription}
            </Typography>
          )}
          {(tweak.edits || []).map((edit, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 0.5, fontSize: 11, fontFamily: 'monospace', color: '#555', mb: 0.25 }}>
              <span style={{ color: '#e53e3e', textDecoration: 'line-through' }}>{edit.property}: {edit.oldValue}</span>
              <span>→</span>
              <span style={{ color: '#38a169' }}>{edit.property}: {edit.newValue}</span>
            </Box>
          ))}
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            {!applied && (
              <Button
                size="small" variant="contained"
                startIcon={<CheckIcon sx={{ fontSize: 12 }} />}
                onClick={() => onApply(tweak)}
                sx={{ fontSize: 11, py: 0.25, px: 1.5, borderRadius: 1, textTransform: 'none', background: '#1a1a1a', '&:hover': { background: '#333' } }}
              >
                Apply
              </Button>
            )}
            <IconButton size="small" onClick={() => onDismiss(tweak.id)} sx={{ color: '#ccc', '&:hover': { color: '#e53e3e' } }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

const TweaksPanel = ({ projectId, onApplyComplete }) => {
  const [tweaks, setTweaks] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [status, setStatus] = useState('');
  const [applied, setApplied] = useState(new Set());
  const [dismissed, setDismissed] = useState(new Set());
  const [applyingAll, setApplyingAll] = useState(false);

  const handleAnalyze = useCallback(async () => {
    setAnalyzing(true);
    setTweaks([]);
    setApplied(new Set());
    setDismissed(new Set());
    setStatus('Starting analysis…');

    try {
      const stream = designStudioService.analyzeDesign(projectId);
      for await (const event of stream.events()) {
        if (event.status) setStatus(event.status);
        if (event.done) {
          setTweaks(event.tweaks || []);
          setStatus('');
        }
        if (event.error) {
          setStatus(`Error: ${event.content}`);
        }
      }
    } catch (err) {
      setStatus(`Error: ${err.message}`);
    } finally {
      setAnalyzing(false);
    }
  }, [projectId]);

  const handleApply = useCallback(async (tweak) => {
    try {
      await designStudioService.applyTweak(projectId, tweak.edits);
      setApplied((prev) => new Set([...prev, tweak.id]));
      onApplyComplete?.();
    } catch (err) {
      console.error('Apply tweak failed:', err);
    }
  }, [projectId, onApplyComplete]);

  const handleApplyAll = useCallback(async () => {
    setApplyingAll(true);
    const pending = tweaks.filter((t) => !applied.has(t.id) && !dismissed.has(t.id));
    for (const tweak of pending) {
      try {
        await designStudioService.applyTweak(projectId, tweak.edits);
        setApplied((prev) => new Set([...prev, tweak.id]));
      } catch {}
    }
    setApplyingAll(false);
    onApplyComplete?.();
  }, [tweaks, applied, dismissed, projectId, onApplyComplete]);

  const handleDismiss = useCallback((id) => {
    setDismissed((prev) => new Set([...prev, id]));
  }, []);

  const visible = tweaks.filter((t) => !dismissed.has(t.id));
  const pendingCount = visible.filter((t) => !applied.has(t.id)).length;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee', flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 13, mb: 1, color: '#1a1a1a' }}>
          AI Tweaks
        </Typography>
        <Button
          fullWidth variant="contained" size="small"
          startIcon={analyzing ? <CircularProgress size={12} color="inherit" /> : <AutoFixHighIcon sx={{ fontSize: 14 }} />}
          onClick={handleAnalyze}
          disabled={analyzing}
          sx={{ background: '#6366f1', borderRadius: 1.5, textTransform: 'none', fontSize: 12, '&:hover': { background: '#4f46e5' } }}
        >
          {analyzing ? (status || 'Analyzing…') : 'Analyze design'}
        </Button>

        {pendingCount > 0 && (
          <Button
            fullWidth variant="outlined" size="small"
            startIcon={applyingAll ? <CircularProgress size={12} /> : <CheckIcon sx={{ fontSize: 14 }} />}
            onClick={handleApplyAll}
            disabled={applyingAll}
            sx={{ mt: 1, borderRadius: 1.5, textTransform: 'none', fontSize: 12 }}
          >
            {applyingAll ? 'Applying…' : `Apply ${pendingCount} direct edits`}
          </Button>
        )}
      </Box>

      {/* List */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1 }}>
        {!analyzing && visible.length === 0 && (
          <Typography variant="body2" sx={{ color: '#aaa', fontSize: 12, textAlign: 'center', mt: 4 }}>
            {tweaks.length > 0 ? 'All tweaks applied or dismissed.' : 'Click "Analyze design" to get AI suggestions.'}
          </Typography>
        )}
        {visible.map((tweak) => (
          <TweakItem
            key={tweak.id}
            tweak={tweak}
            applied={applied.has(tweak.id)}
            onApply={handleApply}
            onDismiss={handleDismiss}
          />
        ))}
      </Box>
    </Box>
  );
};

export default TweaksPanel;
